/// <reference path="../types.d.ts" />

import { createReducer } from '@reduxjs/toolkit';
import InitialState from './initial-state';
import * as dictionaryValues from '../actions/dictionary-values';
import * as dictionary from '../actions/dictionary';
import * as dictionaryRequest from '../actions/dictionary-request.actions';

import { LOAD_STATES } from './common';

import { jumpContext } from '../actions/contexts.action';
import { logout } from '../actions/ui';
import { commonEnums } from '@codametrix/ui-common';
import {
  columnMetadataDataItem,
  defaultDictionary
} from '../tests/fixtures/stubs/dictionary';
import {
  createDataGridRows,
  dictionaryMetadataFieldGroup
} from './state/dictionary-values';
import { syncActions } from '../actions/dictionary-values';
import { formErrors } from '../stubs/form-errors';
const { DictionaryStatus, FilterTypes } = commonEnums;

const createColumns = (
  columns: CMxAPI.ColumnDefinition[],
  statusFilter: string
) => {
  let dataGridCols = [];
  dataGridCols.push(
    ...columns
      .sort((a, b) => a.columnIndex - b.columnIndex)
      .map(column => {
        return {
          key: column.name,
          displayName: column.name,
          headerComponentParams: column.keyColumnIndicator
            ? { menuIcon: 'fa-key', displayName: column.name }
            : undefined
        };
      })
  );

  if (statusFilter === DictionaryStatus.PUBLISHED) {
    dataGridCols.push({
      key: 'remove',
      action: 'remove',
      displayName: 'Archive',
      isSortable: false,
      defaultSort: 'NONE'
    });
  }

  return dataGridCols;
};

const createSearchForms = (columns: CMxAPI.ColumnDefinition[]) => {
  return columns.map(column => {
    return {
      label: column.name,
      key: column.name,
      type: FilterTypes.LIKE
    };
  });
};

const createRowEditForm = (cols: CMxAPI.ColumnDefinition[]) => {
  const valueFields = cols.map(col => {
    return {
      label: col.name,
      key: col.name,
      required: true,
      helpText: '',
      type: 'text',
      readonly: false
    };
  });

  const cellFieldGroup: CMxCommonApp.FieldGroup = {
    label: 'Cells',
    fields: valueFields
  };

  const rowEditFormButtons = [
    { buttonType: 'submit', buttonText: 'Save' },
    { buttonType: 'link', buttonText: 'Back' }
  ];

  return {
    fieldGroups: [dictionaryMetadataFieldGroup, cellFieldGroup],
    buttons: rowEditFormButtons
  };
};

const createRowAddForm = (cols: CMxAPI.ColumnDefinition[]) => {
  const valueFields = cols.map(col => {
    return {
      label: col.name,
      key: col.name,
      required: true,
      helpText: '',
      type: 'text',
      readonly: false
    };
  });

  const cellFieldGroup: CMxCommonApp.FieldGroup = {
    label: 'This row will be added to the end of the dictionary.',
    fields: valueFields
  };

  const rowAddFormButtons = [
    { buttonType: 'submit', buttonText: 'Save' },
    { buttonType: 'link', buttonText: 'Back' }
  ];

  return {
    fieldGroups: [cellFieldGroup],
    buttons: rowAddFormButtons
  };
};

const reducer = createReducer(InitialState.dictionaryValues, builder => {
  // Default cases from cmxReducerFactory: reset state on logout or context jump
  builder.addCase(logout.type, () => ({ ...InitialState.dictionaryValues }));
  builder.addCase(jumpContext.pending.type, () => ({
    ...InitialState.dictionaryValues
  }));

  builder.addCase(dictionaryValues.listItems.pending, state => {
    return {
      ...state,
      items: [],
      loadState: LOAD_STATES.initial,
      formErrors: formErrors.noErrors
    };
  });
  builder.addCase(dictionaryValues.listItems.rejected, state => {
    return { ...state, loadState: LOAD_STATES.failed };
  });
  builder.addCase(dictionaryValues.listItems.fulfilled, (state, action) => {
    const result = action.payload;
    const { sortablePageable } = state;
    const dataGridRows = createDataGridRows(result.content);

    const sp = {
      ...sortablePageable,
      pageableDefinition: {
        ...result,
        items: dataGridRows
      }
    };

    return {
      ...state,
      sortablePageable: sp,
      items: dataGridRows,
      pageable: result,
      loadState: LOAD_STATES.done
    };
  });

  builder.addCase(dictionary.loadDictionary.pending, state => {
    const sortablePageable =
      state.sortablePageable ?? InitialState.dictionary.sortablePageable;
    return {
      ...state,
      sortablePageable,
      items: [],
      loadState: LOAD_STATES.initial,
      criteriaLoadState: LOAD_STATES.initial,
      formErrors: formErrors.noErrors,
      isDeleteModalOpen: false,
      updatedRows: InitialState.dictionaryValues.updatedRows
    };
  });

  builder.addCase(dictionaryValues.loadDictionary.fulfilled, (state, action) => {
    const params = action.meta.arg;
    const dictionary = action.payload;
    const statusFilter =
      params.sortablePageable.filterableOptions.filters?.find(
        filter => filter.key === 'status'
      )?.terms[0] || '';
    const columns = createColumns(dictionary.columnDefinitions, statusFilter);
    const searchForms = createSearchForms(dictionary.columnDefinitions);
    const rowEditForm = createRowEditForm(dictionary.columnDefinitions);
    const rowAddForm = createRowAddForm(dictionary.columnDefinitions);

    const criteriaFilter = {
      ...state.criteriaFilter,
      filters: params.sortablePageable?.filterableOptions.filters || [],
      dateFilter: undefined,
      searchForms
    };

    return {
      ...state,
      criteriaFilter,
      columnDefs: columns,
      selectedDictionary: {
        ...dictionary,
        crossMapIndicator: dictionary.crossMapIndicator ?? false
      },
      rowEditForm,
      rowAddForm,
      criteriaLoadState: LOAD_STATES.done,
      showDraft: false
    };
  });

  builder.addCase(dictionaryValues.saveDictionaryRow.pending, state => {
    return { ...state, loadState: LOAD_STATES.initial };
  });
  builder.addCase(dictionaryValues.saveDictionaryRow.rejected, state => {
    return { ...state, loadState: LOAD_STATES.failed };
  });
  builder.addCase(dictionaryValues.saveDictionaryRow.fulfilled, (state, action) => {
    return {
      ...state,
      activeItem: action.payload,
      loadState: LOAD_STATES.done,
      showDraft: true
    };
  });

  builder.addCase(dictionaryValues.loadValue.pending, (state, action) => {
    return {
      ...state,
      loadState: LOAD_STATES.initial,
      selectedRowIndex: action.meta.arg.data
    };
  });
  builder.addCase(dictionaryValues.loadValue.rejected, state => {
    return { ...state, loadState: LOAD_STATES.failed };
  });
  builder.addCase(dictionaryValues.loadValue.fulfilled, (state, action) => {
    return { ...state, selectedRowIndex: action.payload, loadState: LOAD_STATES.done };
  });

  builder.addCase(dictionary.listItems.pending, (state, action) => {
    return {
      ...state,
      criteriaFilter: {
        ...state.criteriaFilter,
        filters: [
          { key: 'status', terms: [DictionaryStatus.PUBLISHED], type: 'IN' }
        ]
      },
      sortablePageable: {
        ...state.sortablePageable,
        pageableDefinition: {
          ...state.sortablePageable.pageableDefinition,
          pageable: {
            sort: {
              sorted: false,
              unsorted: true,
              empty: true
            },
            offset: 0,
            pageSize: 25,
            pageNumber: 0,
            unpaged: false,
            paged: true
          },
          size: 25
        },
        filterableOptions: {
          filters: [
            { key: 'status', terms: [DictionaryStatus.PUBLISHED], type: 'IN' }
          ]
        }
      },
      selectedRowIndex: -1,
      selectedDictionary: defaultDictionary,
      loadState: LOAD_STATES.initial,
      formErrors: formErrors.noErrors
    };
  });

  builder.addCase(dictionaryRequest.uploadDictionary.pending, (state, action) => {
    const statusFilter = !state.isCorrection
      ? [
          {
            key: 'status',
            terms: ['draft'],
            type: 'IN'
          }
        ]
      : state.criteriaFilter.filters;
    return {
      ...state,
      criteriaFilter: {
        ...state.criteriaFilter,
        filters: statusFilter
      },
      sortablePageable: {
        ...state.sortablePageable,
        filterableOptions: {
          filters: statusFilter
        },

        pageableDefinition: {
          ...state.sortablePageable.pageableDefinition,
          pageable: {
            sort: {
              sorted: false,
              unsorted: true,
              empty: true
            },
            offset: 0,
            pageSize: 25,
            pageNumber: 0,
            unpaged: false,
            paged: true
          },
          size: 25
        }
      },
      formErrors: formErrors.noErrors
    };
  });

  builder.addCase(dictionaryRequest.uploadDictionary.rejected, (state, action) => {
    const e = action.error as any;
    const fe: CMxCommonApp.FormErrors = {
      ...e.errors,
      errors: [
        e?.errors?.message
          ? 'Error: ' + e.errors.message
          : 'Error: ' + e.message
      ]
    };
    return {
      ...state,
      loadState: LOAD_STATES.failed,
      formErrors: fe
    };
  });

  builder.addCase(dictionaryRequest.uploadDictionary.fulfilled, state => {
    return {
      ...state,
      loadState: LOAD_STATES.done,
      selectedRowIndex: -1
    };
  });

  builder.addCase(dictionaryValues.publishDictionary.pending, state => {
    return {
      ...state,
      loadState: LOAD_STATES.initial,
      criteriaLoadState: LOAD_STATES.initial
    };
  });
  builder.addCase(dictionaryValues.publishDictionary.rejected, state => {
    return {
      ...state,
      loadState: LOAD_STATES.done,
      criteriaLoadState: LOAD_STATES.done
    };
  });
  builder.addCase(dictionaryValues.publishDictionary.fulfilled, (state, action) => {
    return {
      ...state,
      criteriaFilter: {
        ...state.criteriaFilter,
        filters: [
          { key: 'status', terms: [DictionaryStatus.PUBLISHED], type: 'IN' }
        ]
      },
      sortablePageable: {
        ...state.sortablePageable,
        pageableDefinition: {
          ...state.sortablePageable.pageableDefinition,
          pageable: {
            sort: {
              sorted: false,
              unsorted: true,
              empty: true
            },
            offset: 0,
            pageSize: 25,
            pageNumber: 0,
            unpaged: false,
            paged: true
          },
          size: 25
        },
        filterableOptions: {
          filters: [
            { key: 'status', terms: [DictionaryStatus.PUBLISHED], type: 'IN' }
          ]
        }
      },
      loadState: LOAD_STATES.done,
      criteriaLoadState: LOAD_STATES.done
    };
  });

  builder.addCase(syncActions.setStatusFilter, (state, action) => {
    const status = action.payload;
    return {
      ...state,
      criteriaFilter: {
        ...state.criteriaFilter,
        filters: [{ key: 'status', terms: [status], type: 'IN' }]
      },
      sortablePageable: {
        ...state.sortablePageable,
        filterableOptions: {
          filters: [{ key: 'status', terms: [status], type: 'IN' }]
        }
      },
      loadState: LOAD_STATES.initial,
      criteriaLoadState: LOAD_STATES.done
    };
  });

  builder.addCase(dictionaryValues.initializeColumnMetadataEditor.pending, state => {
    const { columnMetadataEditor, selectedDictionary } = state;
    return {
      ...state,
      columnMetadataEditor: {
        ...columnMetadataEditor,
        loadState: LOAD_STATES.initial,
        containsDictionaryMappings: selectedDictionary.crossMapIndicator,
        columnMetadata: selectedDictionary.columnDefinitions
      }
    };
  });

  builder.addCase(dictionaryValues.initializeColumnMetadataEditor.rejected, state => {
    const { columnMetadataEditor } = state;
    return {
      ...state,
      columnMetadataEditor: {
        ...columnMetadataEditor,
        loadState: LOAD_STATES.failed
      }
    };
  });

  builder.addCase(dictionaryValues.initializeColumnMetadataEditor.fulfilled, (state, action) => {
    const result = action.payload;
    const { columnMetadataEditor } = state;
    let columnMetadata = columnMetadataEditor.columnMetadata;
    // if columnMetadata has no already been set, set it to result
    if (!columnMetadata?.length) {
      columnMetadata = result;
    }
    return {
      ...state,
      columnMetadataEditor: {
        ...columnMetadataEditor,
        columnMetadata: columnMetadata,
        loadState: LOAD_STATES.done
      }
    };
  });

  builder.addCase(dictionaryValues.listDictionaryHistory.pending, state => {
    return {
      ...state,
      historyLoadState: LOAD_STATES.initial,
      listDictionaryHistory: []
    };
  });
  builder.addCase(dictionaryValues.listDictionaryHistory.rejected, state => {
    return {
      ...state,
      historyLoadState: LOAD_STATES.failed,
      listDictionaryHistory: []
    };
  });
  builder.addCase(dictionaryValues.listDictionaryHistory.fulfilled, (state, action) => {
    return {
      ...state,
      historyLoadState: LOAD_STATES.done,
      listDictionaryHistory: action.payload.map(res => {
        const {
          id,
          dictionaryCsvS3Uri,
          requestStatus,
          requestType,
          updatedByFullName,
          updatedDate
        } = res;
        return {
          id,
          dictionaryCsvS3Uri,
          requestStatus,
          requestType,
          updatedByFullName,
          updatedDate
        };
      }) as CMxAPI.DictionaryHistory[]
    };
  });

  builder.addCase(dictionaryValues.listDictionaryOptions.fulfilled, (state, action: any) => {
    return {
      ...state,
      selectedTenantName: action.payload.organizationName
    };
  });

  builder.addCase(syncActions.selectKeyColumn, (state, action) => {
    const result = action.payload;
    const { columnMetadataEditor } = state;

    const columnMetadata = columnMetadataEditor.columnMetadata.map(col => {
      return { ...col, keyColumnIndicator: col.columnIndex === result };
    });
    return {
      ...state,
      columnMetadataEditor: {
        ...columnMetadataEditor,
        columnMetadata
      }
    };
  });

  builder.addCase(syncActions.selectActiveColumn, (state, action) => {
    const result = action.payload;
    const { columnMetadataEditor } = state;

    const referenceColumn =
      result.cellReferences[0]?.lookupProperty || undefined;
    const referenceDictionary =
      result.cellReferences[0]?.dictionaryName || undefined;
    const isRefValueList = result.cellReferences[0]?.isRefValueList || false;
    const referenceTenant =
      result.cellReferences[0]?.organizationName || undefined;
    const textEditable = result.cellReferences[0]?.textEditable || false;

    return {
      ...state,
      columnMetadataEditor: {
        ...columnMetadataEditor,
        activeColumn: result,
        activeColumnMetadataObj: {
          ...columnMetadataDataItem,
          columnName: result.name,
          referenceColumn,
          referenceDictionary,
          referenceTenant,
          isRefValueList,
          textEditable
        }
      }
    };
  });

  builder.addCase(dictionaryValues.listDictionaryOptions.pending, (state, action) => {
    const res = action.meta.arg;
    return {
      ...state,
      columnMetadataEditor: {
        ...state.columnMetadataEditor,
        activeColumnMetadataObj: {
          ...state.columnMetadataEditor.activeColumnMetadataObj,
          referenceTenant: res.tenantId
        }
      }
    };
  });

  builder.addCase(dictionaryValues.listColumnOptions.pending, (state, action) => {
    const res = action.meta.arg;
    return {
      ...state,
      columnMetadataEditor: {
        ...state.columnMetadataEditor,
        activeColumnMetadataObj: {
          ...state.columnMetadataEditor.activeColumnMetadataObj,
          referenceDictionary: res.dictionaryName
        }
      }
    };
  });

  builder.addCase(dictionaryValues.chooseColumnReference, (state, action) => {
    const res = action.payload;
    return {
      ...state,
      columnMetadataEditor: {
        ...state.columnMetadataEditor,
        activeColumnMetadataObj: {
          ...state.columnMetadataEditor.activeColumnMetadataObj,
          referenceColumn: res
        }
      }
    };
  });

  builder.addCase(syncActions.saveColumnMapping, (state, action) => {
    const res = action.payload;
    const { columnMetadataEditor, selectedDictionary } = state;
    const {
      referenceTenant,
      referenceColumn,
      referenceDictionary,
      isRefValueList,
      textEditable
    } = res.mappingInfo;

    let organizationName = referenceTenant;
    if (
      organizationName !== state.selectedTenantName &&
      state.selectedTenantName !== ''
    ) {
      organizationName = state.selectedTenantName;
    }

    let parentOrganizationName = null;
    if (
      ['CPT Codes', 'Modifiers'].includes(referenceDictionary!) &&
      organizationName !== 'CodaMetrix'
    ) {
      parentOrganizationName = 'Codametrix';
    }

    const cellReference: CMxAPI.CellReference = {
      parentOrganizationName: parentOrganizationName || null,
      organizationName: organizationName || '',
      dictionaryName: referenceDictionary || '',
      lookupProperty: referenceColumn || '',
      id: -1,
      columnDefinitionId: res.columnInfo.id,
      isRefValueList: isRefValueList,
      textEditable: textEditable
    };

    const columnMetadata = columnMetadataEditor.columnMetadata.map(col => {
      if (col.id === res.columnInfo.id) {
        return {
          ...col,
          cellReferences: [cellReference]
        };
      }
      return col;
    });

    let updatedRows =
      state.updatedRows ?? state.sortablePageable.pageableDefinition.content;

    updatedRows = updatedRows.map(row => {
      const cells = row.cells.map(cell => {
        return cell.columnDefinitionId === res.columnInfo.id
          ? {
              ...cell,
              columnDefinition: {
                ...cell.columnDefinition,
                cellReferences: [cellReference]
              }
            }
          : cell;
      });
      return { ...row, cells };
    });

    return {
      ...state,
      columnMetadataEditor: {
        ...columnMetadataEditor,
        columnMetadata
      },
      selectedDictionary: {
        ...selectedDictionary,
        columnDefinitions: columnMetadata
      },
      updatedRows
    };
  });

  builder.addCase(syncActions.removeColumnMapping, (state, action) => {
    const res = action.payload;
    const { columnMetadataEditor } = state;

    const columnMetadata = columnMetadataEditor.columnMetadata.map(col => {
      if (col.id === res.id) {
        return {
          ...col,
          cellReferences: []
        };
      }
      return col;
    });

    let updatedRows =
      state.updatedRows ?? state.sortablePageable.pageableDefinition.content;

    updatedRows = updatedRows?.map(row => {
      const cells = row.cells.map(cell => {
        return cell.columnDefinitionId === res.id
          ? {
              ...cell,
              columnDefinition: {
                ...cell.columnDefinition,
                cellReferences: []
              }
            }
          : cell;
      });
      return { ...row, cells };
    });

    return {
      ...state,
      columnMetadataEditor: {
        ...columnMetadataEditor,
        columnMetadata
      },
      updatedRows
    };
  });

  builder.addCase(dictionaryValues.saveColumnDefinitions.rejected, (state, action) => {
    const { columnMetadataEditor } = state;
    const response = action.error as any;

    return {
      ...state,
      colErrors: response.errors,
      columnMetadataEditor: {
        ...columnMetadataEditor,
        loadState: LOAD_STATES.failed
      }
    };
  });

  builder.addCase(dictionaryValues.saveColumnDefinitions.fulfilled, (state, action) => {
    return {
      ...state,
      selectedDictionary: action.payload,
      updatedRows: InitialState.dictionaryValues.updatedRows
    };
  });

  builder.addCase(dictionary.updatePublishedMetadata.fulfilled, (state, action) => {
    return {
      ...state,
      updatedRows: InitialState.dictionaryValues.updatedRows
    };
  });

  builder.addCase(syncActions.toggleArchiveModal, (state, action) => {
    const isOpen = action.payload;
    return {
      ...state,
      archiveModal: {
        ...state.archiveModal,
        isOpen,
        archiveResult: []
      }
    };
  });

  builder.addCase(dictionaryValues.selectArchiveRow.pending, state => {
    return {
      ...state,
      archiveModal: {
        ...state.archiveModal,
        loadState: LOAD_STATES.initial
      }
    };
  });

  builder.addCase(dictionaryValues.selectArchiveRow.fulfilled, (state, action) => {
    return {
      ...state,
      archiveModal: {
        ...state.archiveModal,
        selectedRow: action.payload,
        loadState: LOAD_STATES.done
      }
    };
  });

  builder.addCase(syncActions.savePotentialArchives, (state, action) => {
    return {
      ...state,
      archiveModal: {
        ...state.archiveModal,
        potentialArchives: action.payload
      }
    };
  });

  builder.addCase(dictionaryValues.archiveRow.pending, state => {
    return {
      ...state,
      archiveModal: {
        ...state.archiveModal,
        loadState: LOAD_STATES.started
      }
    };
  });

  builder.addCase(dictionaryValues.archiveRow.fulfilled, (state, action) => {
    return {
      ...state,
      archiveModal: {
        ...state.archiveModal,
        archiveResult: action.payload,
        loadState: LOAD_STATES.done
      }
    };
  });

  builder.addCase(dictionaryValues.toggleDeleteModal, (state, action) => {
    return {
      ...state,
      isDeleteModalOpen: action.payload
    };
  });

  builder.addCase(dictionaryValues.toggleUploadType, (state, action) => {
    return {
      ...state,
      isCorrection: action.payload
    };
  });

  builder.addCase(dictionaryValues.toggleInfoModal, (state, action) => {
    return {
      ...state,
      isInfoModalOpen: action.payload
    };
  });

  builder.addCase(dictionaryValues.toggleSecurityModal, (state, action) => {
    return {
      ...state,
      isSecurityModalOpen: action.payload
    };
  });

  builder.addCase(dictionaryValues.togglePublishModal, (state, action) => {
    return {
      ...state,
      isPublishModalOpen: action.payload
    };
  });

  builder.addCase(dictionaryValues.updateDictionaryDescription.fulfilled, (state, action) => {
    return {
      ...state,
      selectedDictionary: action.payload
    };
  });

  builder.addCase(syncActions.updateDictionaryValue, (state, action) => {
    const payload = action.payload;
    return {
      ...state,
      columnDefs: payload?.colDefs ?? state.columnDefs,
      selectedDictionary:
        payload?.selectedDictionary ?? state.selectedDictionary,
      selectedRowIndex: payload?.selectedRowIndex ?? state.selectedRowIndex,
      items: payload.items ?? state.items
    };
  });

  builder.addCase(dictionaryValues.listColumnValues.fulfilled, (state, action) => {
    const result = action.payload;
    return {
      ...state,
      mappedColValues: [
        ...state.mappedColValues,
        { key: result[0].columnName, values: result }
      ]
    };
  });
});

export default reducer;
